import { crm } from "../../../theme";

// Domínio "situacaoDuimp" da API DUIMP + marcadores do robô (SiscomexHandler)
const SITUATIONS: Record<string, { label: string; tone: Tone }> = {
  REGISTRADA_AGUARDANDO_CANAL: { label: "Registrada, aguardando canal", tone: "info" },
  EM_CONFERENCIA_SELECIONADA: { label: "Em conferência", tone: "warning" },
  DESEMBARACADA_AGUARDANDO_PENDENCIA_TRIBUTOS_ESTADUAIS: { label: "Desembaraçada, aguardando ICMS", tone: "warning" },
  DESEMBARACADA_AGUARDANDO_ENTREGA_CARGA: { label: "Desembaraçada, aguardando entrega", tone: "success" },
  DESEMBARACADA_CARGA_ENTREGUE: { label: "Desembaraçada, carga entregue", tone: "success" },
  ENTREGA_ANTECIPADA_AGUARDANDO_PENDENCIA_TRIBUTOS_ESTADUAIS: { label: "Entrega antecipada, aguardando ICMS", tone: "warning" },
  ENTREGA_ANTECIPADA_AGUARDANDO_ENTREGA_CARGA: { label: "Entrega antecipada, aguardando entrega", tone: "success" },
  ENTREGA_ANTECIPADA_CARGA_ENTREGUE: { label: "Entrega antecipada, carga entregue", tone: "success" },
  CANCELADA_PELA_ADUANA: { label: "Cancelada pela aduana", tone: "danger" },
  CANCELADA_POR_APURACAO: { label: "Cancelada por apuração", tone: "danger" },
  NAO_ENCONTRADA: { label: "Não encontrada no Portal Único", tone: "danger" },
  SEM_ACESSO: { label: "Sem acesso no Portal Único", tone: "danger" },
};

export type Tone = "info" | "success" | "warning" | "danger" | "neutral";

/** Soft background + strong text per tone (Pill colors). */
export const toneColors: Record<Tone, { bg: string; color: string }> = {
  info: { bg: crm.brandSoft, color: crm.brandDark },
  success: { bg: "#ecfdf3", color: crm.success },
  warning: { bg: "#fffbeb", color: crm.warning },
  danger: { bg: "#fef2f2", color: crm.danger },
  neutral: { bg: crm.subtle, color: crm.textMuted },
};

export const duimpSituation = (value?: string) =>
  value
    ? SITUATIONS[value] ?? { label: value.replace(/_/g, " ").toLowerCase(), tone: "neutral" as Tone }
    : undefined;

/** Canal de parametrização (VERDE, AMARELO, VERMELHO, CINZA). */
export const channelTone = (channel?: string): Tone => {
  switch (channel?.toUpperCase()) {
    case "VERDE": return "success";
    case "AMARELO": return "warning";
    case "VERMELHO": return "danger";
    default: return "neutral";
  }
};

export const brl = (value?: number | null) =>
  value == null ? "—" : value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const money = (value?: number | null, currency?: string | null) => {
  if (value == null) return "—";
  if (!currency || currency === "BRL") return brl(value);
  try {
    return value.toLocaleString("pt-BR", { style: "currency", currency });
  } catch {
    return `${currency} ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
  }
};

const humanize = (value: string) => {
  const text = value.replace(/_/g, " ").toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Domínio "situacaoLicenciamento"
const LICENSING: Record<string, { label: string; tone: Tone }> = {
  DISPENSADO: { label: "Dispensado", tone: "neutral" },
  DEFERIDO: { label: "Deferido", tone: "success" },
  PENDENTE: { label: "Pendente", tone: "warning" },
  IMPEDIDO: { label: "Impedido", tone: "danger" },
};

export const licensingSituation = (value?: string) =>
  value ? LICENSING[value] ?? { label: humanize(value), tone: "neutral" as Tone } : undefined;

/** Conferência aduaneira/anuente (situacaoConferenciaAduaneira/Anuente). */
export const inspectionSituation = (value?: string): { label: string; tone: Tone } => {
  if (!value) return { label: "—", tone: "neutral" };
  const upper = value.toUpperCase();
  const tone: Tone = upper.includes("CANCELADA") || upper.includes("INTERROMPIDA") || upper.includes("SEM_PROSSEGUIMENTO")
    ? "danger"
    : upper.startsWith("CONCLUIDA") || upper.includes("AUTORIZADO")
    ? "success"
    : upper.includes("EXIGENCIA") || upper.includes("ANALISE") || upper.includes("CONFERENCIA") || upper.includes("AGUARDANDO")
    ? "warning"
    : "neutral";
  return { label: humanize(value), tone };
};

/** Situação do LPCO: descrição do TALPCO ("Deferido") ou marcador do robô. */
export const lpcoSituation = (value?: string): { label: string; tone: Tone } => {
  if (!value) return { label: "Não consultado", tone: "neutral" };
  if (value === "SEM_ACESSO") return { label: "Sem acesso", tone: "danger" };
  if (value === "NAO_ENCONTRADO") return { label: "Não encontrado", tone: "danger" };
  const upper = value.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase();
  const tone: Tone = upper.includes("INDEFERID") || upper.includes("CANCELAD") || upper.includes("VENCID")
    ? "danger"
    : upper.includes("DEFERID")
    ? "success"
    : "warning";
  return { label: humanize(value), tone };
};

export const cargoControlLabel = (value?: string) => (value ? humanize(value) : undefined);
